import React, { useEffect, useMemo, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Checkbox,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControl,
  FormControlLabel,
  IconButton,
  InputLabel,
  Link,
  MenuItem,
  Paper,
  Select,
  Stack,
  Switch,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TableSortLabel,
  Tooltip,
  Typography,
  useMediaQuery,
  useTheme,
} from '@mui/material';
import {
  Download as DownloadIcon,
  Refresh as RefreshIcon,
  TrendingDown as TrendingDownIcon,
  WarningAmber as WarningIcon,
  Visibility as VisibilityIcon,
  Undo as UndoIcon,
  Block as BlockIcon,
  CheckCircleOutline as ApplyIcon,
  OpenInNew as OpenInNewIcon,
  ImageNotSupportedOutlined as NoImageIcon,
  ArrowUpward as ArrowUpwardIcon,
  ArrowDownward as ArrowDownwardIcon,
  DoneAll as DoneAllIcon,
  InfoOutlined as InfoIcon,
} from '@mui/icons-material';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Link as RouterLink, useNavigate, useSearchParams } from 'react-router-dom';
import { integrationApiService, CatalogPriceChangeRow } from '../services/integrationApi';
import { useBrandRegistry } from '../hooks/useBrandRegistry';
import { makeApiCall, getAuthHeaders } from '../utils/apiConfig';
import {
  canApplyBlocked,
  canRevertApplied,
  catalogPriceChangeRowKey,
  groupCatalogPriceChangeRows,
  isFlatPriceRow,
  isHeldPriceMove,
  retailRevertChange,
  resolveCatalogPriceChangeProductId,
  rowsToResolveAfterPriceAction,
} from '../utils/catalogPriceChangeActions';
import { useOrgFeatureFlag } from '../hooks/useOrgFeatureFlags';
import { ConfirmDialog, LoadingState, PageContainer, PageHeader, StatusChip, StatusTone } from '../components/ui';
import { useToast } from '../contexts/ToastContext';
import { brandDisplayName } from '../utils/brandDisplay';

const VALID_ACTIONS = new Set([
  'default',
  'all',
  'applied_needs_review',
  'rejected_suspicious',
  'fast_tracked',
  'staged_for_review',
  'staged_updated',
  'no_change',
  'error',
]);

function parseDaysParam(raw: string | null): number {
  const n = Number(raw);
  if (n === 1 || n === 7 || n === 30 || n === 90) return n;
  return 7;
}

const ACTION_LABELS: Record<string, string> = {
  applied_needs_review: 'Needs review',
  rejected_suspicious: 'Blocked (suspicious)',
  fast_tracked: 'Fast-tracked',
  staged_for_review: 'Staged',
  staged_updated: 'Staged update',
  no_change: 'No change',
  error: 'Error',
};

const actionTone = (action: string): StatusTone => {
  if (action === 'applied_needs_review') return 'caution';
  if (action === 'rejected_suspicious') return 'problem';
  return 'neutral';
};

/** Stable identity of a change row: report doc id + index in its changes array. */
const rowKey = catalogPriceChangeRowKey;

function formatMoney(n: number): string {
  if (!Number.isFinite(n)) return '—';
  return `$${n.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
}

function formatWhen(iso: string): string {
  if (!iso) return '—';
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return '—';
  return d.toLocaleString('en-US', {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
    hour: 'numeric',
    minute: '2-digit',
  });
}

const resolveProductId = resolveCatalogPriceChangeProductId;

/** Direction + magnitude for the Δ% cells: red ↓ for drops, green ↑ for rises. */
const DeltaPercent: React.FC<{ row: CatalogPriceChangeRow }> = ({ row }) => {
  if (isFlatPriceRow(row) || !Number.isFinite(row.changePercent)) {
    return (
      <Typography variant="body2" color="text.secondary" component="span">
        —
      </Typography>
    );
  }
  const down = row.oldPrice > 0 ? row.newPrice < row.oldPrice : row.changePercent < 0;
  return (
    <Box
      component="span"
      sx={{
        display: 'inline-flex',
        alignItems: 'center',
        gap: 0.25,
        fontSize: '0.875rem',
        color: down ? 'error.main' : 'success.main',
        fontWeight: row.suspicious ? 700 : 500,
      }}
    >
      {down ? <ArrowDownwardIcon sx={{ fontSize: 14 }} /> : <ArrowUpwardIcon sx={{ fontSize: 14 }} />}
      {Math.abs(row.changePercent).toFixed(1)}%
    </Box>
  );
};

type PendingAction = {
  row: CatalogPriceChangeRow;
  kind: 'deny' | 'revert' | 'apply' | 'approve_held' | 'reject_held';
};

function matchMethodLabel(method: 'url' | 'image' | 'agent_sku' | undefined): string {
  if (method === 'url') return 'the stored page URL';
  if (method === 'image') return 'the product image';
  if (method === 'agent_sku') return 'the item code on the page';
  return 'the price check';
}

/** Subset of the canonical product used by the in-page review dialog. */
interface ReviewProduct {
  id: string;
  sku?: string;
  upc?: string;
  name?: string;
  brand?: string;
  model?: string;
  category?: string;
  status?: string;
  pricing?: {
    msrp?: number;
    map?: number;
    retail?: number;
    salePrice?: number;
    currency?: string;
    priceAsOf?: string;
    priceLastChecked?: string;
  };
  images?: Array<{ url: string; alt?: string; isPrimary?: boolean }>;
  source?: {
    vendorName?: string;
    sourceUrl?: string;
  };
}

function primaryImageUrl(product: ReviewProduct | null | undefined): string | null {
  const images = product?.images || [];
  const primary = images.find((img) => img.isPrimary && img.url) || images.find((img) => img.url);
  return primary?.url || null;
}

type PriceChangeSortId = 'reportedAt' | 'changePercent' | 'brandId' | 'sku';

function parseSortParam(raw: string | null): PriceChangeSortId {
  if (raw === 'changePercent' || raw === 'brandId' || raw === 'sku' || raw === 'reportedAt') return raw;
  return 'reportedAt';
}

function parseSortDir(raw: string | null): 'asc' | 'desc' {
  return raw === 'asc' ? 'asc' : 'desc';
}

export const AdminCatalogPriceChangesPage: React.FC = () => {
  const theme = useTheme();
  const fullScreenDialog = useMediaQuery(theme.breakpoints.down('sm'));
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const queryClient = useQueryClient();
  const { data: brandRegistry } = useBrandRegistry();
  const [days, setDays] = useState<number>(() => parseDaysParam(searchParams.get('days')));
  const [brandId, setBrandId] = useState<string>(() => {
    const brand = (searchParams.get('brand') || '').trim().toLowerCase();
    return brand || 'all';
  });
  const [region, setRegion] = useState<string>(() => {
    const r = (searchParams.get('region') || '').trim().toLowerCase();
    return r || 'all';
  });
  const [action, setAction] = useState<string>(() => {
    const a = (searchParams.get('action') || '').trim();
    return VALID_ACTIONS.has(a) ? a : 'default';
  });
  const [suspiciousOnly, setSuspiciousOnly] = useState(
    () => searchParams.get('suspicious') === '1' || searchParams.get('suspicious') === 'true'
  );
  const [includeResolved, setIncludeResolved] = useState(
    () => searchParams.get('resolved') === '1' || searchParams.get('resolved') === 'true'
  );
  /** Default: newest reported first. */
  const [sortBy, setSortBy] = useState<PriceChangeSortId>(() => parseSortParam(searchParams.get('sort')));
  const [sortDir, setSortDir] = useState<'asc' | 'desc'>(() => parseSortDir(searchParams.get('dir')));
  const [exporting, setExporting] = useState(false);
  const [exportError, setExportError] = useState('');
  const [pending, setPending] = useState<PendingAction | null>(null);
  const [reviewRow, setReviewRow] = useState<CatalogPriceChangeRow | null>(null);
  const [selected, setSelected] = useState<Set<string>>(new Set());
  const { showToast } = useToast();
  const { enabled: deviationReviewOn, isLoading: deviationReviewLoading } = useOrgFeatureFlag('price_deviation_review');
  const heldReviewUi = (row: CatalogPriceChangeRow) =>
    isHeldPriceMove(row) && (deviationReviewOn || deviationReviewLoading);
  const heldReviewActions = (row: CatalogPriceChangeRow) =>
    isHeldPriceMove(row) && deviationReviewOn && !row.resolved;

  // Keep filters in sync when navigating here from a notification deep-link.
  useEffect(() => {
    const nextDays = parseDaysParam(searchParams.get('days'));
    const nextBrand = (searchParams.get('brand') || '').trim().toLowerCase() || 'all';
    const nextRegion = (searchParams.get('region') || '').trim().toLowerCase() || 'all';
    const rawAction = (searchParams.get('action') || '').trim();
    const nextAction = VALID_ACTIONS.has(rawAction) ? rawAction : 'default';
    const nextSuspicious =
      searchParams.get('suspicious') === '1' || searchParams.get('suspicious') === 'true';
    const nextIncludeResolved =
      searchParams.get('resolved') === '1' || searchParams.get('resolved') === 'true';

    const nextSort = parseSortParam(searchParams.get('sort'));
    const nextDir = parseSortDir(searchParams.get('dir'));

    if (days !== nextDays) setDays(nextDays);
    if (brandId !== nextBrand) setBrandId(nextBrand);
    if (region !== nextRegion) setRegion(nextRegion);
    if (action !== nextAction) setAction(nextAction);
    if (suspiciousOnly !== nextSuspicious) setSuspiciousOnly(nextSuspicious);
    if (includeResolved !== nextIncludeResolved) setIncludeResolved(nextIncludeResolved);
    if (sortBy !== nextSort) setSortBy(nextSort);
    if (searchParams.has('dir') && sortDir !== nextDir) setSortDir(nextDir);
  }, [searchParams]);

  // Mirror filter changes back into the URL so links are shareable / reload-safe.
  useEffect(() => {
    const next = new URLSearchParams();
    if (days !== 7) next.set('days', String(days));
    if (brandId !== 'all') next.set('brand', brandId);
    if (region !== 'all') next.set('region', region);
    if (action !== 'default') next.set('action', action);
    if (suspiciousOnly) next.set('suspicious', '1');
    if (includeResolved) next.set('resolved', '1');
    if (sortBy !== 'reportedAt') next.set('sort', sortBy);
    if (!(sortBy === 'reportedAt' && sortDir === 'desc')) next.set('dir', sortDir);

    const nextQs = next.toString();
    if (nextQs !== searchParams.toString()) {
      setSearchParams(next, { replace: true });
    }
  }, [days, brandId, region, action, suspiciousOnly, includeResolved, sortBy, sortDir, searchParams, setSearchParams]);

  const brands = useMemo(() => {
    const list = (brandRegistry || [])
      .map((b: any) => ({
        id: String(b.slug || b.id || b.brandId || '').toLowerCase(),
        name: String(b.name || b.brandName || b.slug || b.id || ''),
      }))
      .filter((b) => b.id)
      .sort((a, b) => a.name.localeCompare(b.name));
    return list;
  }, [brandRegistry]);

  const filters = useMemo(
    () => ({
      days,
      brandId: brandId !== 'all' ? brandId : undefined,
      region: region !== 'all' ? region : undefined,
      action: action === 'default' ? undefined : action === 'all' ? 'all' : action,
      suspiciousOnly: suspiciousOnly || undefined,
      includeResolved: includeResolved || undefined,
      limit: 1000,
    }),
    [days, brandId, region, action, suspiciousOnly, includeResolved]
  );

  const { data, isLoading, error, refetch, isFetching } = useQuery({
    queryKey: ['catalog-price-changes', filters],
    queryFn: () => integrationApiService.fetchCatalogPriceChanges(filters),
  });

  const rows: CatalogPriceChangeRow[] = data?.data || [];
  const summary = data?.summary;

  // Drop selections that no longer exist (filter change, refetch after resolve).
  useEffect(() => {
    setSelected((prev) => {
      if (prev.size === 0) return prev;
      const valid = new Set(rows.map(rowKey));
      const next = new Set(Array.from(prev).filter((key) => valid.has(key)));
      return next.size === prev.size ? prev : next;
    });
  }, [rows]);

  /** Collapse repeat scrape blocks of the same SKU, then sort the groups by their displayed (primary) row. */
  const sortedGroups = useMemo(() => {
    const dir = sortDir === 'asc' ? 1 : -1;
    const compareRows = (a: CatalogPriceChangeRow, b: CatalogPriceChangeRow) => {
      let cmp = 0;
      switch (sortBy) {
        case 'changePercent':
          cmp = Math.abs(a.changePercent) - Math.abs(b.changePercent);
          break;
        case 'brandId':
          cmp = a.brandId.localeCompare(b.brandId, undefined, { sensitivity: 'base' });
          break;
        case 'sku':
          cmp = (a.sku || '').localeCompare(b.sku || '', undefined, { sensitivity: 'base' });
          break;
        case 'reportedAt':
        default:
          cmp = (a.reportedAt || '').localeCompare(b.reportedAt || '');
          break;
      }
      if (cmp !== 0) return cmp * dir;
      // Stable secondary: newest date, then largest % move
      const byDate = (b.reportedAt || '').localeCompare(a.reportedAt || '');
      if (byDate !== 0) return byDate;
      return Math.abs(b.changePercent) - Math.abs(a.changePercent);
    };
    const groups = groupCatalogPriceChangeRows(rows);
    return [...groups].sort((a, b) => compareRows(a.primary, b.primary));
  }, [rows, sortBy, sortDir]);

  const selectedGroupCount = useMemo(
    () =>
      sortedGroups.filter((group) =>
        group.members.every((member) => selected.has(rowKey(member)))
      ).length,
    [sortedGroups, selected]
  );

  const selectedAttemptCount = useMemo(
    () =>
      sortedGroups
        .filter((group) => group.members.every((member) => selected.has(rowKey(member))))
        .reduce((sum, group) => sum + group.attemptCount, 0),
    [sortedGroups, selected]
  );

  /** Distinct live products across groups — a SKU with both retail and sale groups is one product. */
  const uniqueProductCount = useMemo(() => {
    const ids = new Set(
      sortedGroups.map(
        (group) =>
          resolveCatalogPriceChangeProductId(group.primary) ||
          `${group.primary.brandId}|${group.primary.sku}`
      )
    );
    return ids.size;
  }, [sortedGroups]);

  const totalAttemptCount = useMemo(
    () => sortedGroups.reduce((sum, group) => sum + group.attemptCount, 0),
    [sortedGroups]
  );

  const toggleSort = (column: PriceChangeSortId) => {
    if (sortBy === column) {
      setSortDir((d) => (d === 'asc' ? 'desc' : 'asc'));
    } else {
      setSortBy(column);
      // Date / % default to descending; text columns to ascending
      setSortDir(column === 'brandId' || column === 'sku' ? 'asc' : 'desc');
    }
  };

  const priceMutation = useMutation({
    mutationFn: async ({
      productId,
      retail,
      resolveItems,
    }: {
      productId: string;
      retail: number;
      resolveItems: Array<{ reportId: string; changeIndex: number }>;
    }) => {
      const headers = await getAuthHeaders();
      const response = await makeApiCall(`/normalization/canonical/${encodeURIComponent(productId)}`, {
        method: 'PUT',
        headers,
        body: JSON.stringify({ pricing: { retail } }),
      });
      const json = await response.json().catch(() => null);
      if (!response.ok) {
        throw new Error(json?.message || json?.error?.message || `Failed to update price (HTTP ${response.status})`);
      }
      let resolvedCount = 0;
      let resolveError: string | undefined;
      if (resolveItems.length > 0) {
        try {
          const result = await integrationApiService.resolveCatalogPriceChanges(resolveItems);
          resolvedCount = result.updatedCount;
        } catch (err) {
          resolveError = err instanceof Error ? err.message : 'Failed to clear the review row';
        }
      }
      return { json, resolvedCount, resolveError };
    },
    onSuccess: (result, variables) => {
      const label = formatMoney(variables.retail);
      if (result.resolveError) {
        showToast(`Retail set to ${label}, but the list did not update: ${result.resolveError}`, 'error');
      } else {
        showToast(result.resolvedCount > 1
              ? `Retail set to ${label}. Cleared ${result.resolvedCount} matching review rows.`
              : `Retail set to ${label}`, 'success');
      }
      setPending(null);
      if (
        reviewRow &&
        variables.resolveItems.some(
          (item) => item.reportId === reviewRow.reportId && item.changeIndex === reviewRow.changeIndex
        )
      ) {
        setReviewRow(null);
      }
      if (result.resolvedCount > 0) {
        const resolvedKeys = new Set(
          variables.resolveItems.map((item) => `${item.reportId}:${item.changeIndex}`)
        );
        queryClient.setQueriesData(
          { queryKey: ['catalog-price-changes'] },
          (old: { data?: CatalogPriceChangeRow[]; count?: number; summary?: { suspiciousCount?: number } } | undefined) => {
            if (!old?.data) return old;
            const removedFromPage = includeResolved
              ? 0
              : old.data.filter((row) => resolvedKeys.has(rowKey(row))).length;
            const nextData = includeResolved
              ? old.data.map((row) =>
                  resolvedKeys.has(rowKey(row)) ? { ...row, resolved: true } : row
                )
              : old.data.filter((row) => !resolvedKeys.has(rowKey(row)));
            return {
              ...old,
              data: nextData,
              count:
                typeof old.count === 'number'
                  ? Math.max(0, old.count - removedFromPage)
                  : old.count,
              summary: old.summary
                ? {
                    ...old.summary,
                    suspiciousCount: nextData.filter((row) => row.suspicious && !row.resolved).length,
                  }
                : old.summary,
            };
          }
        );
      }
      void queryClient.invalidateQueries({ queryKey: ['catalog-price-changes'] });
      // Refresh the review dialog's "current pricing" if it is open.
      void queryClient.invalidateQueries({ queryKey: ['catalog-price-change-product'] });
    },
    onError: (err: Error) => {
      showToast(err.message || 'Price update failed', 'error');
    },
  });

  const deviationMutation = useMutation({
    mutationFn: async ({
      reportId,
      changeIndex,
      decision,
      resolveItems,
    }: {
      reportId: string;
      changeIndex: number;
      decision: 'approved' | 'rejected';
      resolveItems: Array<{ reportId: string; changeIndex: number }>;
    }) => {
      const decided = await integrationApiService.decidePriceDeviationReview({ reportId, changeIndex, decision });
      let resolvedCount = 1;
      if (resolveItems.length > 0) {
        const extra = await integrationApiService.resolveCatalogPriceChanges(resolveItems);
        resolvedCount = Math.max(resolvedCount, extra.updatedCount);
      }
      return { ...decided, resolvedCount };
    },
    onSuccess: (_result, variables) => {
      const row = pending?.row;
      showToast(
        variables.decision === 'approved'
          ? `Wrote the read price ${formatMoney(row?.priceMoveReview?.readRetail ?? row?.newPrice ?? 0)}`
          : 'Rejected the read. The stored price was left as it is.',
        'success',
      );
      setPending(null);
      setReviewRow(null);
      void queryClient.invalidateQueries({ queryKey: ['catalog-price-changes'] });
      void queryClient.invalidateQueries({ queryKey: ['catalog-price-change-product'] });
    },
    onError: (err: Error) => {
      showToast(err.message || 'Could not decide the held price read', 'error');
    },
  });

  const resolveMutation = useMutation({
    mutationFn: (items: Array<{ reportId: string; changeIndex: number }>) =>
      integrationApiService.resolveCatalogPriceChanges(items),
    onSuccess: (result) => {
      showToast(`Resolved ${result.updatedCount} price change${result.updatedCount === 1 ? '' : 's'}`, 'success');
      setSelected(new Set());
      void queryClient.invalidateQueries({ queryKey: ['catalog-price-changes'] });
    },
    onError: (err: Error) => {
      showToast(err.message || 'Failed to resolve price changes', 'error');
    },
  });

  const handleResolveSelected = () => {
    const items = rows
      .filter((row) => selected.has(rowKey(row)))
      .map((row) => ({ reportId: row.reportId, changeIndex: row.changeIndex }));
    if (items.length === 0) return;
    resolveMutation.mutate(items);
  };

  const toggleGroupSelected = (members: CatalogPriceChangeRow[]) => {
    setSelected((prev) => {
      const next = new Set(prev);
      const keys = members.map(rowKey);
      const allSelected = keys.length > 0 && keys.every((key) => next.has(key));
      for (const key of keys) {
        if (allSelected) next.delete(key);
        else next.add(key);
      }
      return next;
    });
  };

  const handleExport = async () => {
    setExporting(true);
    setExportError('');
    try {
      await integrationApiService.downloadCatalogPriceChangesCsv(filters);
    } catch (err: any) {
      setExportError(err?.message || 'CSV export failed');
    } finally {
      setExporting(false);
    }
  };

  // Load the live product for the review dialog. The endpoint resolves stale
  // deterministic ids via ?sku=, same as the Products page deep-link.
  const reviewProductId = reviewRow ? resolveProductId(reviewRow) : null;
  const {
    data: reviewProduct,
    isLoading: reviewLoading,
    error: reviewError,
  } = useQuery<ReviewProduct | null>({
    queryKey: ['catalog-price-change-product', reviewProductId, reviewRow?.sku || ''],
    enabled: !!reviewRow && !!reviewProductId,
    retry: false,
    queryFn: async () => {
      const qs = reviewRow?.sku ? `?sku=${encodeURIComponent(reviewRow.sku)}` : '';
      const response = await makeApiCall(
        `/normalization/canonical/${encodeURIComponent(reviewProductId as string)}${qs}`,
        { headers: await getAuthHeaders() }
      );
      if (response.status === 404) return null;
      const json = await response.json().catch(() => null);
      if (!response.ok) {
        throw new Error(json?.message || json?.error || `Failed to load product (HTTP ${response.status})`);
      }
      return (json?.data || json) as ReviewProduct;
    },
  });

  const openProduct = (row: CatalogPriceChangeRow) => {
    const productId = resolveProductId(row);
    if (!productId && !row.sku) return;
    const params = new URLSearchParams();
    if (productId) params.set('productId', productId);
    // Always pass sku so Products can fall back when productId is a stale
    // brand_sku id and the real doc still uses a random Firestore id.
    if (row.sku) params.set('sku', row.sku);
    navigate(`/app/admin/products?${params.toString()}`);
  };

  const confirmPending = () => {
    if (!pending) return;
    if (pending.kind === 'approve_held' || pending.kind === 'reject_held') {
      deviationMutation.mutate({
        reportId: pending.row.reportId,
        changeIndex: pending.row.changeIndex,
        decision: pending.kind === 'approve_held' ? 'approved' : 'rejected',
        resolveItems: rowsToResolveAfterPriceAction(pending.row, rows),
      });
      return;
    }
    const productId = resolveProductId(pending.row);
    if (!productId) {
      showToast('Could not resolve product id for this SKU — open View product and search manually.', 'error');
      return;
    }
    const revert = retailRevertChange(pending.row);
    if (pending.kind !== 'apply' && (!revert || !canRevertApplied(pending.row))) {
      showToast('No actionable retail baseline is available. Open the product to review its pricing.', 'error');
      return;
    }
    const retail = pending.kind === 'apply' ? pending.row.newPrice : revert!.oldPrice;
    priceMutation.mutate({
      productId,
      retail,
      resolveItems: rowsToResolveAfterPriceAction(pending.row, rows),
    });
  };

  const pendingTitle =
    pending?.kind === 'approve_held'
      ? 'Approve this price read?'
      : pending?.kind === 'reject_held'
        ? 'Reject this price read?'
        : pending?.kind === 'apply'
          ? 'Apply blocked price?'
          : pending?.kind === 'deny'
            ? 'Deny price change?'
            : 'Revert price change?';

  const pendingReview = pending?.row.priceMoveReview;
  const pendingRevert = pending ? retailRevertChange(pending.row) : null;
  const pendingBody = pending
    ? pending.kind === 'approve_held'
      ? `Write ${formatMoney(pendingReview?.readRetail ?? pending.row.newPrice)} as the live retail` +
        (pendingReview?.readList != null ? ` and ${formatMoney(pendingReview.readList)} as the list` : '') +
        ` for ${pending.row.name || pending.row.sku}? The stored price stays unlocked.`
      : pending.kind === 'reject_held'
        ? `Leave ${pending.row.name || pending.row.sku} at ${formatMoney(pendingReview?.storedRetail ?? pending.row.oldPrice)} and clear this review?`
        : pending.kind === 'apply'
          ? `Apply the scraped price ${formatMoney(pending.row.newPrice)} on ${pending.row.name || pending.row.sku}? The scrape previously blocked this as suspicious.`
          : pendingRevert ? `Restore retail to ${formatMoney(pendingRevert.oldPrice)} for ${pending.row.name || pending.row.sku}? Applied retail was ${formatMoney(pendingRevert.newPrice)}.`
            : 'No actionable retail baseline is available. Open the product to review its pricing.'
    : '';

  return (
    <PageContainer>
      <PageHeader
        title="Catalog Price Changes"
        description={
          <>
            Recent retail price moves from scrapes and catalog updates.{' '}
            {deviationReviewOn
              ? 'Review suspicious drops, and approve or reject a manufacturer read the deviation guard held. '
              : 'Review suspicious drops before they go live. '}
            <Tooltip
              enterTouchDelay={0}
              leaveTouchDelay={8000}
              slotProps={{ tooltip: { sx: { maxWidth: 420 } } }}
              title={
                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                  <Typography variant="body2" color="inherit">
                    Use Suspicious only to find bad drops like $1,499 → $50.
                  </Typography>
                  <Typography variant="body2" color="inherit">
                    Repeat blocks of the same SKU are grouped — a "Tried N×" chip means later scrapes hit the same drop
                    again.
                  </Typography>
                  <Typography variant="body2" color="inherit">
                    Apply, Deny, or Revert updates live retail and clears matching rows. Select groups and Resolve to
                    dismiss without changing price.
                  </Typography>
                  {deviationReviewOn && (
                    <Typography variant="body2" color="inherit">
                      A manufacturer read held for review can be approved, which writes that price without locking it,
                      or rejected, which leaves the stored price.
                    </Typography>
                  )}
                </Box>
              }
            >
              <Link
                component="button"
                type="button"
                variant="body2"
                underline="hover"
                sx={{ display: 'inline-flex', alignItems: 'center', gap: 0.5, verticalAlign: 'baseline' }}
              >
                <InfoIcon sx={{ fontSize: 16 }} />
                How this works
              </Link>
            </Tooltip>
          </>
        }
        actions={
          <Stack direction="row" spacing={1}>
            {selected.size > 0 && (
              <Button
                variant="outlined"
                color="success"
                startIcon={
                  resolveMutation.isPending ? <CircularProgress size={16} color="inherit" /> : <DoneAllIcon />
                }
                onClick={handleResolveSelected}
                disabled={resolveMutation.isPending}
              >
                {selectedGroupCount > 0 && selectedAttemptCount > selectedGroupCount
                  ? `Resolve ${selectedGroupCount} selected (${selectedAttemptCount} attempts)`
                  : `Resolve ${selectedGroupCount || selected.size} selected`}
              </Button>
            )}
            <Button
              variant="outlined"
              startIcon={isFetching ? <CircularProgress size={16} /> : <RefreshIcon />}
              onClick={() => refetch()}
              disabled={isFetching}
            >
              Refresh
            </Button>
            <Button
              variant="contained"
              startIcon={exporting ? <CircularProgress size={16} color="inherit" /> : <DownloadIcon />}
              onClick={handleExport}
              disabled={exporting || isLoading}
            >
              Export CSV
            </Button>
          </Stack>
        }
      />

      <Paper sx={{ p: 2, mb: 2 }}>
        <Stack direction="row" useFlexGap flexWrap="wrap" gap={1.5} alignItems="center">
          <FormControl size="small" sx={{ minWidth: 130 }}>
            <InputLabel>Window</InputLabel>
            <Select
              label="Window"
              value={days}
              onChange={(e) => setDays(Number(e.target.value))}
            >
              <MenuItem value={7}>Last 7 days</MenuItem>
              <MenuItem value={30}>Last 30 days</MenuItem>
              <MenuItem value={90}>Last 90 days</MenuItem>
            </Select>
          </FormControl>

          <FormControl size="small" sx={{ minWidth: 150 }}>
            <InputLabel>Brand</InputLabel>
            <Select
              label="Brand"
              value={brandId}
              onChange={(e) => setBrandId(e.target.value)}
            >
              <MenuItem value="all">All brands</MenuItem>
              {brands.map((b) => (
                <MenuItem key={b.id} value={b.id}>
                  {b.name}
                </MenuItem>
              ))}
            </Select>
          </FormControl>

          <FormControl size="small" sx={{ minWidth: 110 }}>
            <InputLabel>Region</InputLabel>
            <Select
              label="Region"
              value={region}
              onChange={(e) => setRegion(e.target.value)}
            >
              <MenuItem value="all">All</MenuItem>
              <MenuItem value="US">US</MenuItem>
              <MenuItem value="CA">CA</MenuItem>
            </Select>
          </FormControl>

          <FormControl size="small" sx={{ minWidth: 170 }}>
            <InputLabel>Action</InputLabel>
            <Select
              label="Action"
              value={action}
              onChange={(e) => setAction(e.target.value)}
            >
              <MenuItem value="default">Applied / blocked</MenuItem>
              <MenuItem value="applied_needs_review">Needs review only</MenuItem>
              <MenuItem value="rejected_suspicious">Blocked (suspicious) only</MenuItem>
              <MenuItem value="fast_tracked">Fast-tracked only</MenuItem>
              <MenuItem value="all">All actions</MenuItem>
            </Select>
          </FormControl>

          <FormControl size="small" sx={{ minWidth: 160 }}>
            <InputLabel>Sort by</InputLabel>
            <Select
              label="Sort by"
              value={sortBy}
              onChange={(e) => {
                const next = e.target.value as PriceChangeSortId;
                setSortBy(next);
                setSortDir(next === 'brandId' || next === 'sku' ? 'asc' : 'desc');
              }}
            >
              <MenuItem value="reportedAt">Date (newest first)</MenuItem>
              <MenuItem value="changePercent">Change % (largest)</MenuItem>
              <MenuItem value="brandId">Brand</MenuItem>
              <MenuItem value="sku">SKU</MenuItem>
            </Select>
          </FormControl>

          <FormControlLabel
            control={
              <Switch
                size="small"
                checked={suspiciousOnly}
                onChange={(e) => setSuspiciousOnly(e.target.checked)}
                color="warning"
              />
            }
            label="Suspicious only"
            sx={{ mx: 0 }}
          />
          <FormControlLabel
            control={
              <Switch
                size="small"
                checked={includeResolved}
                onChange={(e) => setIncludeResolved(e.target.checked)}
              />
            }
            label="Show resolved"
            sx={{ mx: 0 }}
          />
        </Stack>
      </Paper>

      {summary && (
        <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap sx={{ mb: 2 }}>
          <Chip
            label={
              sortedGroups.length !== (data?.count ?? 0)
                ? `${uniqueProductCount} products · ${totalAttemptCount} attempts`
                : `${data?.count ?? 0} changes`
            }
            color="primary"
            variant="outlined"
          />
          <Chip
            icon={<WarningIcon />}
            label={`${summary.suspiciousCount} suspicious`}
            color={summary.suspiciousCount > 0 ? 'warning' : 'default'}
            variant="outlined"
          />
          <Chip label={`${summary.reportsScanned} reports scanned`} variant="outlined" />
          <Chip
            label={`${Object.keys(summary.byBrand || {}).length} brands`}
            variant="outlined"
          />
        </Stack>
      )}

      {exportError && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setExportError('')}>
          {exportError}
        </Alert>
      )}

      {error && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {(error as Error).message || 'Failed to load catalog price changes'}
        </Alert>
      )}

      {isLoading ? (
        <LoadingState size="page" />
      ) : sortedGroups.length === 0 ? (
        <Alert severity="info">
          No catalog price changes found for this window. Run a brand scrape or widen the filters.
        </Alert>
      ) : (
        <TableContainer component={Paper}>
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell padding="checkbox">
                  <Checkbox
                    size="small"
                    indeterminate={selected.size > 0 && selected.size < rows.length}
                    checked={rows.length > 0 && selected.size === rows.length}
                    onChange={(e) =>
                      setSelected(e.target.checked ? new Set(rows.map(rowKey)) : new Set())
                    }
                    inputProps={{ 'aria-label': 'Select all price changes' }}
                  />
                </TableCell>
                <TableCell sortDirection={sortBy === 'brandId' ? sortDir : false}>
                  <TableSortLabel
                    active={sortBy === 'brandId'}
                    direction={sortBy === 'brandId' ? sortDir : 'asc'}
                    onClick={() => toggleSort('brandId')}
                  >
                    Brand
                  </TableSortLabel>
                </TableCell>
                <TableCell sortDirection={sortBy === 'sku' ? sortDir : false}>
                  <TableSortLabel
                    active={sortBy === 'sku'}
                    direction={sortBy === 'sku' ? sortDir : 'asc'}
                    onClick={() => toggleSort('sku')}
                  >
                    SKU
                  </TableSortLabel>
                </TableCell>
                <TableCell>Name</TableCell>
                <TableCell>Price</TableCell>
                <TableCell align="right">Old</TableCell>
                <TableCell align="right">New</TableCell>
                <TableCell align="right" sortDirection={sortBy === 'changePercent' ? sortDir : false}>
                  <TableSortLabel
                    active={sortBy === 'changePercent'}
                    direction={sortBy === 'changePercent' ? sortDir : 'desc'}
                    onClick={() => toggleSort('changePercent')}
                  >
                    Δ%
                  </TableSortLabel>
                </TableCell>
                <TableCell>Status</TableCell>
                <TableCell>Region</TableCell>
                <TableCell sortDirection={sortBy === 'reportedAt' ? sortDir : false}>
                  <TableSortLabel
                    active={sortBy === 'reportedAt'}
                    direction={sortBy === 'reportedAt' ? sortDir : 'desc'}
                    onClick={() => toggleSort('reportedAt')}
                  >
                    Reported
                  </TableSortLabel>
                </TableCell>
                <TableCell>Job</TableCell>
                <TableCell align="right">Actions</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {sortedGroups.map((group) => {
                const row = group.primary;
                const productId = resolveProductId(row);
                const revertable = canRevertApplied(row);
                const applicable = canApplyBlocked(row) && !heldReviewUi(row);
                const deviationActions = heldReviewActions(row);
                const groupSelected =
                  group.members.length > 0 &&
                  group.members.every((member) => selected.has(rowKey(member)));
                const attemptJobs = group.members
                  .map((m) => m.jobId)
                  .filter(Boolean)
                  .slice(0, 4);
                return (
                  <TableRow
                    key={rowKey(row)}
                    selected={groupSelected}
                    sx={
                      row.suspicious
                        ? { bgcolor: (theme) => theme.palette.warning.main + '14' }
                        : undefined
                    }
                  >
                    <TableCell padding="checkbox">
                      <Checkbox
                        size="small"
                        checked={groupSelected}
                        onChange={() => toggleGroupSelected(group.members)}
                        inputProps={{ 'aria-label': `Select ${row.sku || row.name}` }}
                      />
                    </TableCell>
                    <TableCell sx={{ textTransform: 'capitalize' }}>{brandDisplayName(row.brandId)}</TableCell>
                    <TableCell>
                      <Typography variant="body2" fontFamily="monospace">
                        {row.sku || '—'}
                      </Typography>
                    </TableCell>
                    <TableCell sx={{ maxWidth: 380 }}>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75, minWidth: 0 }}>
                        <Typography variant="body2" noWrap title={row.name} sx={{ minWidth: 0 }}>
                          {row.name || '—'}
                        </Typography>
                        {row.suspicious && (
                          <Chip
                            size="small"
                            icon={<TrendingDownIcon />}
                            label="Suspicious"
                            color="warning"
                            sx={{ flexShrink: 0 }}
                          />
                        )}
                        {group.attemptCount > 1 && (
                          <Tooltip
                            title={`Scrapes tried this ${group.attemptCount} times${
                              attemptJobs.length
                                ? ` (jobs: ${attemptJobs.map((id) => id.slice(0, 8)).join(', ')}${
                                    group.attemptCount > attemptJobs.length ? ', …' : ''
                                  })`
                                : ''
                            }. Showing the newest; Resolve / Apply clears all.`}
                          >
                            <Chip
                              size="small"
                              label={`Tried ${group.attemptCount}×`}
                              color="info"
                              variant="outlined"
                              sx={{ flexShrink: 0 }}
                            />
                          </Tooltip>
                        )}
                      </Box>
                    </TableCell>
                    <TableCell>
                      {/* Which price moved — retail, or a sale being applied/changed. */}
                      <Chip
                        size="small"
                        label={row.rawPriceChanges?.length ? 'Displayed price' : row.field === 'sale' ? 'Sale' : 'Retail'}
                        color={row.field === 'sale' ? 'info' : 'default'}
                        variant="outlined"
                      />
                    </TableCell>
                    <TableCell align="right">
                      {row.field === 'sale' && row.oldPrice <= 0 ? (
                        <Typography variant="body2" color="text.secondary" component="span">
                          No sale
                        </Typography>
                      ) : (
                        formatMoney(row.oldPrice)
                      )}
                    </TableCell>
                    <TableCell align="right">{formatMoney(row.newPrice)}</TableCell>
                    <TableCell align="right">
                      <DeltaPercent row={row} />
                    </TableCell>
                    <TableCell sx={{ whiteSpace: 'nowrap' }}>
                      <StatusChip
                        size="small"
                        label={ACTION_LABELS[row.action] || row.action}
                        tone={actionTone(row.action)}
                        variant="outlined"
                      />
                      {row.resolved && (
                        <Chip
                          size="small"
                          label="Resolved"
                          color="success"
                          variant="outlined"
                          sx={{ ml: 0.5 }}
                        />
                      )}
                      {row.discontinued && (
                        <Chip
                          size="small"
                          label="Discontinued"
                          color="default"
                          variant="outlined"
                          sx={{ ml: 0.5 }}
                        />
                      )}
                    </TableCell>
                    <TableCell>{row.region}</TableCell>
                    <TableCell sx={{ whiteSpace: 'nowrap' }}>
                      {formatWhen(row.reportedAt)}
                      {group.attemptCount > 1 && (
                        <Typography variant="caption" color="text.secondary" sx={{ ml: 0.75 }}>
                          latest of {group.attemptCount}
                        </Typography>
                      )}
                    </TableCell>
                    <TableCell>
                      {row.jobId ? (
                        <Link
                          component={RouterLink}
                          to={`/app/admin/jobs?jobId=${encodeURIComponent(row.jobId)}`}
                          underline="hover"
                          variant="body2"
                        >
                          {row.jobId.slice(0, 8)}…
                        </Link>
                      ) : (
                        '—'
                      )}
                    </TableCell>
                    <TableCell align="right">
                      <Stack direction="row" spacing={0.25} justifyContent="flex-end">
                        <Tooltip title={productId || row.sku ? 'Review product & price change' : 'No product id/SKU'}>
                          <span>
                            <IconButton
                              size="small"
                              onClick={() => setReviewRow(row)}
                              disabled={!productId && !row.sku}
                              aria-label="Review product"
                            >
                              <VisibilityIcon fontSize="small" />
                            </IconButton>
                          </span>
                        </Tooltip>
                        {revertable && (
                          <>
                            <Tooltip title={`Deny change — restore retail ${formatMoney(retailRevertChange(row)!.oldPrice)}`}>
                              <IconButton
                                size="small"
                                color="error"
                                onClick={() => setPending({ row, kind: 'deny' })}
                                aria-label="Deny price change"
                              >
                                <BlockIcon fontSize="small" />
                              </IconButton>
                            </Tooltip>
                            <Tooltip title={`Revert retail to ${formatMoney(retailRevertChange(row)!.oldPrice)}`}>
                              <IconButton
                                size="small"
                                color="warning"
                                onClick={() => setPending({ row, kind: 'revert' })}
                                aria-label="Revert price change"
                              >
                                <UndoIcon fontSize="small" />
                              </IconButton>
                            </Tooltip>
                          </>
                        )}
                        {applicable && (
                          <Tooltip title={`Apply blocked price ${formatMoney(row.newPrice)}`}>
                            <IconButton
                              size="small"
                              color="success"
                              onClick={() => setPending({ row, kind: 'apply' })}
                              aria-label="Apply blocked price"
                            >
                              <ApplyIcon fontSize="small" />
                            </IconButton>
                          </Tooltip>
                        )}
                        {deviationActions && (
                          <>
                            <Tooltip title={`Approve read price ${formatMoney(row.priceMoveReview?.readRetail ?? row.newPrice)}`}>
                              <IconButton
                                size="small"
                                color="success"
                                onClick={() => setPending({ row, kind: 'approve_held' })}
                                aria-label="Approve held price"
                              >
                                <ApplyIcon fontSize="small" />
                              </IconButton>
                            </Tooltip>
                            <Tooltip title={`Reject this read and keep ${formatMoney(row.priceMoveReview?.storedRetail ?? row.oldPrice)}`}>
                              <IconButton
                                size="small"
                                color="error"
                                onClick={() => setPending({ row, kind: 'reject_held' })}
                                aria-label="Reject held price"
                              >
                                <BlockIcon fontSize="small" />
                              </IconButton>
                            </Tooltip>
                          </>
                        )}
                      </Stack>
                    </TableCell>
                  </TableRow>
                );
              })}
            </TableBody>
          </Table>
        </TableContainer>
      )}

      {/* Product review dialog: product context + the price change side by side. */}
      <Dialog
        open={!!reviewRow}
        onClose={() => setReviewRow(null)}
        maxWidth="md"
        fullWidth
        fullScreen={fullScreenDialog}
      >
        {reviewRow && (
          <>
            <DialogTitle sx={{ pb: 1 }}>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: 2 }}>
                <Box>
                  <Typography variant="h6" component="div">
                    {reviewProduct?.name || reviewRow.name || reviewRow.sku || 'Product'}
                  </Typography>
                  <Typography variant="caption" color="text.secondary">
                    {brandDisplayName(reviewProduct?.brand || reviewRow.brandId) + (reviewRow.sku ? ` • SKU: ${reviewRow.sku}` : '')}
                    {reviewProduct?.upc ? ` • UPC: ${reviewProduct.upc}` : ''}
                  </Typography>
                </Box>
                <Stack direction="row" spacing={0.5}>
                  {reviewProduct?.status && (
                    <StatusChip
                      size="small"
                      label={reviewProduct.status}
                      tone={reviewProduct.status === 'discontinued' ? 'caution' : 'neutral'}
                      variant="outlined"
                    />
                  )}
                  {reviewRow.discontinued && (
                    <Chip size="small" label="Discontinued in scrape" color="warning" variant="outlined" />
                  )}
                </Stack>
              </Box>
            </DialogTitle>
            <DialogContent dividers>
              {reviewLoading ? (
                <LoadingState />
              ) : (
                <Box sx={{ display: 'flex', gap: 3, flexWrap: { xs: 'wrap', sm: 'nowrap' } }}>
                  {/* Product image */}
                  <Box
                    sx={{
                      width: 180,
                      minWidth: 180,
                      height: 180,
                      borderRadius: 2,
                      border: '1px solid',
                      borderColor: 'divider',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      overflow: 'hidden',
                      backgroundColor: 'grey.50',
                    }}
                  >
                    {primaryImageUrl(reviewProduct) ? (
                      <Box
                        component="img"
                        src={primaryImageUrl(reviewProduct) as string}
                        alt={reviewProduct?.name || reviewRow.name}
                        sx={{ maxWidth: '100%', maxHeight: '100%', objectFit: 'contain' }}
                      />
                    ) : (
                      <NoImageIcon sx={{ fontSize: 48, color: 'text.disabled' }} />
                    )}
                  </Box>

                  <Box sx={{ flex: 1, minWidth: 260 }}>
                    {/* The change under review */}
                    <Paper variant="outlined" sx={{ p: 1.5, mb: 2, borderRadius: 2 }}>
                      <Typography variant="overline" color="text.secondary">
                        {reviewRow.rawPriceChanges?.length ? 'Reported pricing changes' : isFlatPriceRow(reviewRow) && reviewRow.discontinued
                          ? 'Reported discontinuation'
                          : reviewRow.field === 'sale'
                            ? 'Reported sale price change'
                            : 'Reported retail price change'}
                        {' · '}
                        {formatWhen(reviewRow.reportedAt)}
                      </Typography>
                      {isFlatPriceRow(reviewRow) ? (
                        // Not a price move — flagged for another reason (discontinuation).
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, flexWrap: 'wrap' }}>
                          <Typography variant="body1">
                            {reviewRow.discontinued
                              ? 'The manufacturer site no longer lists this product — it was marked discontinued.'
                              : reviewRow.rawPriceChanges?.length ? 'The displayed price stayed unchanged; individual price fields moved.'
                                : 'No price movement was recorded for this entry.'}
                          </Typography>
                          <Typography variant="body2" color="text.secondary">
                            Price unchanged at {formatMoney(reviewRow.newPrice)}
                          </Typography>
                          <StatusChip
                            size="small"
                            label={ACTION_LABELS[reviewRow.action] || reviewRow.action}
                            tone={actionTone(reviewRow.action)}
                            variant="outlined"
                          />
                        </Box>
                      ) : (
                        <Box sx={{ display: 'flex', alignItems: 'baseline', gap: 1.5, flexWrap: 'wrap' }}>
                          {reviewRow.field === 'sale' && reviewRow.oldPrice <= 0 ? (
                            <Typography variant="body1" component="span" color="text.secondary">
                              No previous sale →
                            </Typography>
                          ) : (
                            <Typography variant="h6" component="span" sx={{ textDecoration: 'line-through', color: 'text.secondary' }}>
                              {formatMoney(reviewRow.oldPrice)}
                            </Typography>
                          )}
                          <Typography variant="h6" component="span" fontWeight={700}>
                            {formatMoney(reviewRow.newPrice)}
                          </Typography>
                          <Chip
                            size="small"
                            label={reviewRow.field === 'sale' ? 'Sale price' : 'Retail price'}
                            color={reviewRow.field === 'sale' ? 'info' : 'default'}
                            variant="outlined"
                          />
                          <DeltaPercent row={reviewRow} />
                          <StatusChip
                            size="small"
                            label={ACTION_LABELS[reviewRow.action] || reviewRow.action}
                            tone={actionTone(reviewRow.action)}
                            variant="outlined"
                          />
                          {reviewRow.suspicious && (
                            <Chip size="small" icon={<TrendingDownIcon />} label="Suspicious" color="warning" />
                          )}
                          {reviewRow.resolved && (
                            <Chip size="small" label="Resolved" color="success" variant="outlined" />
                          )}
                        </Box>
                      )}
                      {reviewRow.rawPriceChanges?.map((change) => (
                        <Typography key={change.field} variant="body2" sx={{ mt: 1 }}>
                          {change.field === 'msrp' ? 'MSRP' : change.field === 'salePrice' ? 'Sale' : 'Retail'}
                          {': '}{change.oldPriceIsFallback ? 'reference ' : ''}{formatMoney(change.oldPrice)} → {formatMoney(change.newPrice)}
                          {' ('}{change.changePercent > 0 ? '+' : ''}{change.changePercent.toFixed(1)}%)
                        </Typography>
                      ))}
                      {reviewRow.priceMoveReview && (
                        <Box sx={{ mt: 1.5 }}>
                          <Typography variant="body2">{reviewRow.priceMoveReview.reason}</Typography>
                          <Typography variant="body2" color="text.secondary">
                            Stored {formatMoney(reviewRow.priceMoveReview.storedRetail ?? reviewRow.oldPrice)}
                            {reviewRow.priceMoveReview.storedList != null
                              ? ` / list ${formatMoney(reviewRow.priceMoveReview.storedList)}`
                              : ''}
                            {' · '}
                            Read {formatMoney(reviewRow.priceMoveReview.readRetail ?? reviewRow.newPrice)}
                            {reviewRow.priceMoveReview.readList != null
                              ? ` / list ${formatMoney(reviewRow.priceMoveReview.readList)}`
                              : ''}
                          </Typography>
                          <Typography variant="body2" color="text.secondary">
                            Matched by {matchMethodLabel(reviewRow.priceMoveReview.matchedBy)}
                            {reviewRow.priceMoveReview.officialExactSku
                              ? ' · official brand site, exact SKU'
                              : ' · not an official brand site'}
                          </Typography>
                          {reviewRow.priceMoveReview.sourceUrl && (
                            <Link
                              href={reviewRow.priceMoveReview.sourceUrl}
                              target="_blank"
                              rel="noopener noreferrer"
                              variant="body2"
                              sx={{ display: 'inline-flex', alignItems: 'center', gap: 0.5 }}
                            >
                              Price source <OpenInNewIcon sx={{ fontSize: 14 }} />
                            </Link>
                          )}
                        </Box>
                      )}
                    </Paper>

                    {/* Live catalog pricing for comparison */}
                    {reviewProduct ? (
                      <>
                        <Typography variant="overline" color="text.secondary">
                          Current catalog pricing
                          {reviewProduct.pricing?.currency ? ` (${reviewProduct.pricing.currency})` : ''}
                        </Typography>
                        <Box
                          sx={{
                            display: 'grid',
                            gridTemplateColumns: {
                              xs: 'repeat(auto-fill, minmax(110px, 1fr))',
                              md: 'repeat(auto-fill, minmax(110px, 1fr))',
                            },
                            gap: 1.5,
                            mb: 1,
                          }}
                        >
                          {(
                            [
                              ['Retail', reviewProduct.pricing?.retail],
                              ['MSRP', reviewProduct.pricing?.msrp],
                              ['MAP', reviewProduct.pricing?.map],
                              ['Sale', reviewProduct.pricing?.salePrice],
                            ] as Array<[string, number | undefined]>
                          )
                            .filter(([, v]) => Number.isFinite(v) && (v as number) > 0)
                            .map(([label, v]) => (
                              <Box key={label}>
                                <Typography variant="caption" color="text.secondary" display="block">
                                  {label}
                                </Typography>
                                <Typography variant="body1" fontWeight={label === 'Retail' ? 700 : 400}>
                                  {formatMoney(v as number)}
                                </Typography>
                              </Box>
                            ))}
                        </Box>
                        {reviewProduct.source?.sourceUrl && (
                          <Link
                            href={reviewProduct.source.sourceUrl}
                            target="_blank"
                            rel="noopener noreferrer"
                            variant="body2"
                            sx={{ display: 'inline-flex', alignItems: 'center', gap: 0.5 }}
                          >
                            Verify on manufacturer site <OpenInNewIcon sx={{ fontSize: 14 }} />
                          </Link>
                        )}
                      </>
                    ) : reviewError ? (
                      <Alert severity="error">
                        Failed to load the product: {reviewError instanceof Error ? reviewError.message : 'unknown error'}
                      </Alert>
                    ) : (
                      <Alert severity="info">
                        This product was not found in the catalog — it may have been removed, or the change
                        came from a staged product. Use "Open full product" to search manually.
                      </Alert>
                    )}
                  </Box>
                </Box>
              )}
            </DialogContent>
            <DialogActions sx={{ px: 3, py: 1.5 }}>
              {canRevertApplied(reviewRow) && (
                <>
                  <Button
                    color="error"
                    startIcon={<BlockIcon />}
                    onClick={() => setPending({ row: reviewRow, kind: 'deny' })}
                  >
                    Deny — restore retail {formatMoney(retailRevertChange(reviewRow)!.oldPrice)}
                  </Button>
                  <Button
                    color="warning"
                    startIcon={<UndoIcon />}
                    onClick={() => setPending({ row: reviewRow, kind: 'revert' })}
                  >
                    Revert
                  </Button>
                </>
              )}
              {canApplyBlocked(reviewRow) && !heldReviewUi(reviewRow) && (
                <Button
                  color="success"
                  startIcon={<ApplyIcon />}
                  onClick={() => setPending({ row: reviewRow, kind: 'apply' })}
                >
                  Apply {formatMoney(reviewRow.newPrice)}
                </Button>
              )}
              {heldReviewActions(reviewRow) && (
                <>
                  <Button
                    color="success"
                    startIcon={<ApplyIcon />}
                    onClick={() => setPending({ row: reviewRow, kind: 'approve_held' })}
                  >
                    Approve {formatMoney(reviewRow.priceMoveReview?.readRetail ?? reviewRow.newPrice)}
                  </Button>
                  <Button
                    color="error"
                    startIcon={<BlockIcon />}
                    onClick={() => setPending({ row: reviewRow, kind: 'reject_held' })}
                  >
                    Reject read
                  </Button>
                </>
              )}
              <Box sx={{ flex: 1 }} />
              <Button onClick={() => openProduct(reviewRow)}>Open full product</Button>
              <Button variant="contained" onClick={() => setReviewRow(null)}>
                Close
              </Button>
            </DialogActions>
          </>
        )}
      </Dialog>

      <ConfirmDialog
        open={!!pending}
        onClose={() => setPending(null)}
        onConfirm={confirmPending}
        title={pendingTitle}
        message={<Typography variant="body2">{pendingBody}</Typography>}
        confirmLabel={
          pending?.kind === 'approve_held'
            ? 'Approve and write price'
            : pending?.kind === 'reject_held'
              ? 'Reject read'
              : pending?.kind === 'apply'
                ? 'Apply new price'
                : pending?.kind === 'deny'
                  ? 'Deny & restore old'
                  : 'Revert to old'
        }
        confirmColor={pending?.kind === 'apply' || pending?.kind === 'approve_held' ? 'primary' : 'error'}
        loading={priceMutation.isPending || deviationMutation.isPending}
      />

    </PageContainer>
  );
};

export default AdminCatalogPriceChangesPage;
